import React, { Component, Fragment } from "react";
import { withTranslation } from 'react-i18next';
import { TFunction, i18n } from 'i18next';
import { PrimaryButton, DefaultButton } from "office-ui-fabric-react/lib/Button";
import { Stack, IStackTokens } from "office-ui-fabric-react/lib/Stack";
import { getTheme } from '@uifabric/styling/lib/styles';
import { GeException, LogMessageType, OnSendNotification, getUrlParameter } from '../../ts/infrastructure';
import { GePortals } from "../../ts/Ge";
import { globalContext } from '../helpers/GlobalContexts';
import { PortalGrid } from './PortalGrid';
import { TooltipHost } from "office-ui-fabric-react/lib/components/Tooltip/TooltipHost";
import { Spinner, SpinnerSize } from "office-ui-fabric-react/lib/Spinner";
import axios, { AxiosError, AxiosResponse } from "axios";
import { EMAIL_REGEX, SERVER_URL, HTTP_REQUEST_TIMEOUT_MS } from "../../ts/infrastructure/constants";
import { OverridesPayload, OverridesResponse, RecipientForDialog, ValidateResponseRecipient, ValidationResponse } from "../../ts/Ge/types";
import { RecipientType } from '../../ts/infrastructure';
import { decodeHtmlEntities, delay, getApmSafeState } from "../../ts/infrastructure/utils";
import { MessageBar, MessageBarType } from "office-ui-fabric-react/lib/MessageBar";

export interface GridItem {
    email: string,
    reason: string,
    type?: string,
    name?: string,
    justification?: string,
    selected?: boolean,
    isJustified?: boolean,
    reason_url?: string | null,
    can_override?: boolean
}

/**
 * This interface defines the state of the side panel component.
 */
interface State {
    gridItems: GridItem[],
    validRecipientsRemaining: boolean,
    loading: boolean,
    blockedRecipients: GePortals.EmailPortals[],
    isOverrideFeautureEnabled: boolean,
    pendingOverrides: boolean,
    syncOverridesFailed: boolean
}

/**
 * This interface defines the props of the side panel component.
 */
interface Props {
    t: TFunction,
    tReady: boolean,
    i18n: i18n
}
/**
 * This component is the top-level component of the side panel.
 */
class PortalSelectorComponent extends Component<Props, State> {
    static contextType = globalContext;
    declare context: React.ContextType<typeof globalContext>;

    protected gePortals: GePortals;
    protected UUID: string;
    protected gridItems: GridItem[] = [];
    protected theme = getTheme();

    state = {
        gridItems: null,
        validRecipientsRemaining: false,
        loading: true,
        pendingOverrides: false,
        syncOverridesFailed: false,
    } as State;

    constructor(props: Props) {
        super(props);
    }

    private sendParentNotification(message: string, success: boolean, headersResponse: OverridesResponse = null) {
        const notification = {
            message,
            success,
            headersResponse
        } as OnSendNotification;
        this.context.globalService.outlookActions.sendParentNotification(notification);
    }

    private onConfirm = async () => {
        const overrides = this.state.gridItems.filter(item => item.isJustified);
        if (overrides.length === 0) {
            this.sendParentNotification("User COMPLETED OPERATION operation", true);
            return;
        }

        const apm = this.context.globalService.apm;
        this.setState({ pendingOverrides: true, syncOverridesFailed: false });
        try {
            const headersResponse = await this.syncOverrides();
            this.setState({ pendingOverrides: false });
            this.sendParentNotification("User COMPLETED OPERATION operation", true, headersResponse);
        } catch (error) {
            this.setState({ syncOverridesFailed: true, pendingOverrides: false });
            this.context.globalService.logger.logMessageWithType(LogMessageType.error, `Applying overrides error: ${error?.message || error}`);
            apm.captureError(error);
        }   
    }

    private onCancel = async () => {
        this.sendParentNotification("User cancelled operation", false);
    }

    public async componentDidMount() {
        const parentTransactionId = getUrlParameter('transaction_id');
        const traceId = getUrlParameter('trace_id');
        // TODO totally remove when re-apply distributedTracing, apm adds this automatically.
        let traceparent = `00-${traceId}-${parentTransactionId}-01`;

        const apm = this.context.globalService.apm;

        // Initialize a new transaction in the iframe connected to the parent transaction
        // check_recipients from modal is recorded with this transaction.
        const transaction = apm.startTransaction('PEP validation', 'modal', { managed: false });

        if (transaction) {
            (transaction as any).traceId = traceId;
            transaction.addLabels({
                traceparent,
                parentTransactionId,
                traceId
            });
            apm.setCustomContext({
                state: getApmSafeState(),
            });
        }

        try {
            this.gePortals = new GePortals(this.context.globalService);
            this.UUID = getUrlParameter('uuid');
            const geApi = this.context.globalService.geApi;

            const recipients = this.gePortals.loadExternalAddresses(this.UUID);
            if (!recipients) this.sendParentNotification("no_recipients", false);

            const recipientsToValidate: string[] = [...new Set(Object.values(recipients).flat())];
            // supply only valid emails to the API.
            const validRecipientstoValidate = recipientsToValidate.filter(email => EMAIL_REGEX.test(email));
            const unresolvedLists: string[] = JSON.parse(localStorage.getItem("unresolvedLists") || "[]");

            if (validRecipientstoValidate.length === 0 && unresolvedLists.length === 0) {
                this.sendParentNotification("no_recipients", false);
                return;
            }

            let blockedRecipients: ValidateResponseRecipient[] = [], isOverrideAllowed = false, response: ValidationResponse;
            // Mock CC recipients as not allowed for local development server and local backend even with manifest pointing elsewhere.
            // Disable this if you want to test with real API calls locally (you would need ABAC/pdp running locally).
            if (__DEV__ && (["local.eshareitar.com", "localhost"].some(host => __PROXY_TARGET__.includes(host)))) {
                await delay(700);
                blockedRecipients = recipients[RecipientType.CC].map((email: string, i: number) => {
                    return {
                        display_name: email.split("@")[0],
                        email,
                        reason: i % 2 === 0 ? `Recipient not allowed with a long reason text demoing long reason style` : `Recipient not allowed`,
                        is_valid: false,
                        can_override: true
                    }
                });
                // Mock unresolved lists as blocked with no override
                unresolvedLists.forEach(name => {
                    blockedRecipients.push({
                        display_name: name,
                        email: name,
                        reason: `List "${name}" recipients cannot be broken down and validated, you need to manually add the recipients.`,
                        is_valid: false,
                        can_override: false
                    });
                });
                isOverrideAllowed = true;
            } else {
                response = await geApi.validateSecureEmailAddresses(validRecipientstoValidate, unresolvedLists);
                blockedRecipients = response.blockedRecipients;
                isOverrideAllowed = response.is_override_allowed || false;
            }

            localStorage.setItem("GeApiState", JSON.stringify(geApi.getState()));

            // check_recipients HTML-escapes recipient text (e.g. "o'malley" -> "o&#x27;malley"),
            // so decode before it reaches the grid or gets persisted for the send-side removal filter.
            blockedRecipients = (blockedRecipients || []).map((recipient) => ({
                ...recipient,
                email: decodeHtmlEntities(recipient.email),
                display_name: recipient.display_name ? decodeHtmlEntities(recipient.display_name) : recipient.display_name
            }));

            blockedRecipients && blockedRecipients.forEach((recipient: GePortals.EmailPortals) => {
                 this.gridItems.push({
                    email: recipient.email ||"",
                    name: recipient.display_name || "",
                    reason: recipient.reason ?? "Recipient not allowed",
                    type: recipient.type,
                    reason_url: recipient.reason_url ?? null,
                    can_override: recipient.can_override !== false
                })
            });


            if (!blockedRecipients?.length) this.sendParentNotification("User COMPLETED OPERATION operation", true);

            let originalBlockedRecipients = blockedRecipients.map((item) => ({
                email: item.email,
                isValid: item.is_valid,
                reason: item.reason,
                isJustified: false,
                justification: undefined
            } as RecipientForDialog));

            this.gePortals.saveBlockedRecipients(originalBlockedRecipients, this.UUID);

            const recipientsRemaining = (validRecipientstoValidate.length + unresolvedLists.length) > blockedRecipients.length;

            this.setState({
                gridItems: Object.assign([], this.gridItems),
                validRecipientsRemaining: recipientsRemaining,
                loading: false,
                isOverrideFeautureEnabled: isOverrideAllowed
            });
        } catch (error) {
            this.context.globalService.apm.captureError(error);

            if (error instanceof GeException) {
                const errId = error.getErrorId();
                if (errId === "hook.no_recipients") {
                    this.sendParentNotification("no_recipients", false);
                }
                else {
                    this.sendParentNotification(error.getErrorLog(), false);
                }
            }
            else if (error instanceof AxiosError) {
                this.sendParentNotification(`${error?.stack} ${error?.status}`, false);                
            }
            else {
                this.sendParentNotification(typeof error === 'string' ? error : error?.message, false);
            }
        }
        finally {
            transaction.end();
        }
    }

    protected handleGridItemsUpdate = (updatedGridItems: GridItem[]) => {
        let blockedRecipients = updatedGridItems.map((item) => ({
            email: item.email,
            isValid: !!item.isJustified,
            reason: item.reason,
            isJustified: !!item.isJustified,
            justification: item.isJustified ? item.justification : undefined
        } as RecipientForDialog));

        this.gePortals.saveBlockedRecipients(blockedRecipients, this.UUID);

        this.setState({
            gridItems: updatedGridItems,
            validRecipientsRemaining: true // Since some recipients were justified
        });
    }

    public async syncOverrides(): Promise<OverridesResponse> {
        const apm = this.context.globalService.apm;
        const overridesUrl = `${SERVER_URL}/api/3.1/organizations/outlook/overrides/`;
        const overrides = this.state.gridItems.filter(item => item.isJustified);
        const {
            subject,
            label
        } = this.context.globalService.geApi;
        
        const overridesData: OverridesPayload = {
            subject: subject || null,
            email_id: null,
            overrides: overrides.map(recipient => ({
                email: recipient.email,
                justification: recipient.justification || '',
                decision: recipient.reason || ''
            })),
            label_id: label?.id || null, // final/elevated label id after validation
            oda_version: 1 // smg versioning
        };

        let response: AxiosResponse<OverridesResponse>;

        try {
            const cwpToken = await this.context.globalService.geApi.getCwpToken();
            const headers = {
                'Content-Type': 'application/json; charset=utf-8',
                'Authorization': 'JWTBEARER ' + cwpToken.token,
                'NCC-DEVICE-IDENTIFIER': 'outlook_pep_addin',
            };

            // Axios defaults to 0 (wait forever). This runs on the send-blocking path after the user
            // confirms overrides, so it must be bounded the same as the rest of AB#14110.
            response = await axios.post(overridesUrl, overridesData, { headers, timeout: HTTP_REQUEST_TIMEOUT_MS });
        } catch (error) {
            if (error?.code === 'ECONNABORTED') {
                apm.getCurrentTransaction()?.addLabels({ "timed-out:syncOverrides": true });
                apm.captureError(new Error(`syncOverrides timed out after ${HTTP_REQUEST_TIMEOUT_MS}ms calling ${overridesUrl}`));
            }

            throw error;
        }

        apm.setCustomContext({ apply_overrides_response: JSON.stringify(response.data) });

        return response.data;
    }

    public render() {
        const { t } = this.props;
        const stackTokens: IStackTokens = { childrenGap: 20 };
        const horizontalStackTokens: IStackTokens = { childrenGap: 20 };
        const { loading, gridItems, validRecipientsRemaining, isOverrideFeautureEnabled, pendingOverrides, syncOverridesFailed } = this.state;

        const headerText = loading
            ? t("portal.loading_text")
            : isOverrideFeautureEnabled ? t("portal.heading_description_override") : t("portal.heading_description");
        const gridClassName = loading ? "ms-Grid loading" : "ms-Grid";

        return (
            <div id="portal-grid-wrapper" style={{
                borderColor: this.theme.palette.neutralSecondary,
                backgroundColor: this.theme.palette.white
            }}>
                <div id="portal-grid" className={gridClassName}>
                <div className="heading-wrapper">
                    <img src={t("portal.icon_url")} alt="Panel secure image" width={window.innerWidth < 550 ? 25 : 50} />
                    <h2 style={{color: t("theme.portal_heading"), marginLeft: "8px"}}>{t("portal.heading")}</h2>
                </div>
                <p className="header-modal-text">
                    {headerText}
                </p>
                    {loading && <Spinner size={SpinnerSize.large} style={{marginTop: "17%"}} />}
                    {gridItems?.length > 0 && !loading &&
                        <Fragment>
                            <div className="ms-Grid-row row-lg grid-wrapper">
                                <PortalGrid
                                    gridItems={gridItems}
                                    canOverride={isOverrideFeautureEnabled}
                                    onGridItemsUpdate={this.handleGridItemsUpdate}
                                />
                            </div>
                            <Stack horizontalAlign="center" className="wrapper-modal-footer">
                                <Stack horizontal horizontalAlign="center" tokens={horizontalStackTokens} className="modal-footer">
                                    <Stack horizontalAlign="center" tokens={stackTokens} className="confirm-buttons ms-Grid-row info" style={{ justifyContent: "center" }}>
                                        <DefaultButton
                                            className="confirm-button cancel"
                                            styles={{
                                                root: {
                                                    border: "1px solid " + t("theme.button_border"),
                                                },
                                            }}
                                            onClick={this.onCancel}>
                                            {t("buttons.cancel")}
                                        </DefaultButton>
                                        <div className="horInfo"><p>{t("buttons.cancel_text")}</p></div>
                                    </Stack>

                                    <Stack horizontalAlign="center" tokens={stackTokens} className="confirm-buttons ms-Grid-row" style={{ justifyContent: "center" }}>
                                        <TooltipHost content={!validRecipientsRemaining ? "All recipients are blocked. Please add at least one valid recipient to proceed." : ""}>
                                            <PrimaryButton
                                                onClick={this.onConfirm}
                                                className="confirm-button send"
                                                disabled={!validRecipientsRemaining || pendingOverrides}
                                                styles={{
                                                    root: {
                                                        fontSize: "18px",
                                                        padding: "20px 35px",
                                                        borderRadius: "6px",
                                                        border: "1px solid " + t("theme.button_border"),
                                                        backgroundColor: t("theme.button_primary")
                                                    },
                                                }}>
                                                {pendingOverrides ? <Spinner size={SpinnerSize.medium} /> : t("buttons.confirm")}
                                            </PrimaryButton>
                                        </TooltipHost>
                                        <div className="horInfo"><p style={{minHeight: "38px"}}>{!pendingOverrides ? t("buttons.confirm_text") : t("portal.sync_overrides_text")}</p></div>
                                    </Stack>
                                </Stack>
                                {syncOverridesFailed &&
                                    <MessageBar messageBarType={MessageBarType.error}>
                                        {t("portal.sync_overrides_failed")}
                                    </MessageBar>
                                }
                            </Stack>
                        </Fragment>
                    }
                </div>
            </div>
        );
    }
}

export default withTranslation()(PortalSelectorComponent);